"use client";

import { forwardRef, useEffect, useState } from "react";

import { POSTS_API } from "@/lib/apis";
import { parsePostCustomContent } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useCommunityContext } from "@/providers/community-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { TCommunityFeedPost } from "@/types/post";
import toast from "react-hot-toast";
import CreatePostModal from "../../post/components/create-post/create-post-modal";
import FeedPostModal from "./feed-post-modal";
import FeedPostUI, { FeedPostUIType } from "./feed-post-ui";
const FeedPost = forwardRef<
	HTMLDivElement,
	{
		post: TCommunityFeedPost;
		classNames?: { card?: string };
		uiType?: FeedPostUIType;
	}
>(({ post: initialPost, classNames, uiType }, ref) => {
	const { user } = useAuth();
	const { openModal } = useModal();
	const { moderatorCommunities } = useCommunityContext();

	const isCommunityModerator = moderatorCommunities.some(
		(community) => community.slug === initialPost.community.slug
	);
	const isChannelModerator = moderatorCommunities.some((community) =>
		community.channels.some((channel) => channel.slug === initialPost.channel.slug)
	);

	const canPinToChannel = user?.isAdmin || isChannelModerator;
	const canPinToCommunity = user?.isAdmin || isCommunityModerator;
	const canNotifyMembers = user?.isAdmin || isCommunityModerator || isChannelModerator;

	const parsedCommunityFeedPost = {
		...initialPost,
		customContent: parsePostCustomContent(initialPost?.customContent),
	};

	const [post, setPost] = useState<TCommunityFeedPost>(initialPost);
	const [showFullPostModal, setShowFullPostModal] = useState(false);
	const [showEditPostModal, setShowEditPostModal] = useState(false);

	const handlePostLike = async (liked: boolean) => {
		if (!user) {
			openModal(ModalType.Auth);
			return !liked;
		}

		const url = new URL(`${POSTS_API}/${initialPost.slug}/reactions`);
		await fetch(url, { method: "PUT" });

		const updatedPost = {
			...post,
			isLikedByUser: liked,
			totalReactions: liked ? post.totalReactions + 1 : post.totalReactions - 1,
		};

		setPost(updatedPost);
		return liked;
	};

	const handleOpenEditPostModal = () => {
		if (post.version === "v2") {
			setShowEditPostModal(true);
		} else {
			toast.error("Unable to edit legacy posts");
		}
	};

	useEffect(() => {
		setPost(initialPost);
	}, [initialPost]);

	if (!post) {
		return null;
	}

	return (
		<div ref={ref}>
			<FeedPostUI
				post={parsedCommunityFeedPost}
				classNames={classNames}
				uiType={uiType}
				onEditPost={handleOpenEditPostModal}
				onPostLike={handlePostLike}
				onShowFullPost={() => setShowFullPostModal(true)}
				isDeletable={user?.isAdmin || user?.id == parsedCommunityFeedPost.author.id}
				isEditable={user?.id === parsedCommunityFeedPost.author.id}
				isPinnable={canPinToChannel || canPinToCommunity}
				canPinToChannel={canPinToChannel}
				canPinToCommunity={canPinToCommunity}
				canNotifyMembers={canNotifyMembers}
			/>
			{showFullPostModal && (
				<FeedPostModal open={showFullPostModal} setOpen={setShowFullPostModal} post={post} />
			)}
			{showEditPostModal && (
				<CreatePostModal
					mode="edit"
					open={showEditPostModal}
					setOpen={setShowEditPostModal}
					defaultValues={{
						postSlug: post.slug,
						communitySlug: post.community.slug ?? "",
						channelSlug: post.channel.slug ?? "",
						title: post.title,
						jsonContent: post.content ?? {},
						htmlContent: post.legacyContent ?? "",
						mentions: [],
						images: post.media,
						links: parsedCommunityFeedPost.customContent.links,
						imageViewType: parsedCommunityFeedPost.customContent.imageViewType ?? "carousel",
						notifyMembers: false,
						postType: parsedCommunityFeedPost.postType ?? "DISCUSSION",
					}}
				/>
			)}
		</div>
	);
});

export default FeedPost;
